import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { LayoutDashboard, Package, FolderTree, FileText, Users, BookOpen, Plus, Pencil, Trash2, Upload, X as XIcon, Menu, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { NIVILABS_LOGO } from "@/lib/api";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const resolveBackendUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const cleaned = String(url).replace(/^\/+/, "");
  return BACKEND_URL ? `${BACKEND_URL}/${cleaned}` : `/${cleaned}`;
};

export function AdminLayout() {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!user) nav("/login");
      else if (user.role !== "admin") nav("/");
    }
  }, [loading, user, nav]);

  if (!user || user.role !== "admin") return null;

  const SidebarLinks = () => (
    <nav className="space-y-1 text-sm">
      <SideLink to="/admin" icon={LayoutDashboard} end label="Dashboard" />
      <SideLink to="/admin/products" icon={Package} label="Products" />
      <SideLink to="/admin/categories" icon={FolderTree} label="Categories" />
      <SideLink to="/admin/inquiries" icon={FileText} label="Inquiries" />
      <SideLink to="/admin/users" icon={Users} label="Users" />
    </nav>
  );

  return (
    <div className="min-h-screen bg-muted/30">

      {/* Mobile Header */}
      <header className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <Link to="/">
          <img
            src={NIVILABS_LOGO}
            alt="nivilabs"
            className="h-10 w-auto"
          />
        </Link>

        <button onClick={() => setSidebarOpen(true)}>
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Mobile Drawer */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-[260px] p-5">
          <Link to="/" onClick={() => setSidebarOpen(false)}>
            <img
              src={NIVILABS_LOGO}
              alt="nivilabs"
              className="h-14 w-auto object-contain mb-8"
            />
          </Link>

          <SidebarLinks />

          <div className="mt-10 p-3 bg-primary/5 rounded-md text-xs text-muted-foreground">
            Logged in as{" "}
            <span className="font-semibold text-foreground">
              {user.name}
            </span>
          </div>
        </SheetContent>
      </Sheet>

      <div className="lg:grid lg:grid-cols-[260px_1fr]">

        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col bg-white border-r border-gray-100 h-screen sticky top-0 p-5">
          <Link to="/">
            <img
              src={NIVILABS_LOGO}
              alt="nivilabs"
              className="h-14 w-auto object-contain mb-8"
            />
          </Link>

          <SidebarLinks />

          <div className="mt-auto p-3 bg-primary/5 rounded-md text-xs text-muted-foreground">
            Logged in as{" "}
            <span className="font-semibold text-foreground">
              {user.name}
            </span>
          </div>
        </aside>

        {/* Content */}
        <main className="p-4 lg:p-10 overflow-x-auto">
          <Outlet />
        </main>

      </div>
    </div>
  );
}
const SideLink = ({ to, end, icon: Icon, label, testid }) => (
  <NavLink end={end} to={to} data-testid={testid} className={({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md ${isActive
      ? "bg-primary text-white"
      : "text-foreground/70 hover:bg-muted"
    }`
  }
  >
    <Icon className="w-4 h-4" />
    {label}
  </NavLink>
);

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="animate-pulse h-96 bg-white rounded-xl" />;
  const tiles = [
    { label: "Total inquiries", value: stats.total_inquiries, hint: `${stats.new_inquiries} new` },
    { label: "Total products", value: stats.total_products, hint: `${stats.low_stock} low stock` },
    { label: "Registered customers", value: stats.total_users },
    { label: "Categories", value: stats.inventory_by_category.length },
  ];
  return (
    <div data-testid="admin-dashboard-page">
      <h1 className="font-display text-3xl font-bold">Dashboard</h1>
      <p className="text-sm text-muted-foreground">Snapshot of nivilabs activity.</p>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <div key={t.label} className="bg-white border border-gray-100 rounded-xl p-5">
            <div className="text-xs text-muted-foreground">{t.label}</div>
            <div className="font-display text-3xl font-bold mt-2 text-primary">{t.value}</div>
            {t.hint && <div className="text-xs text-muted-foreground mt-1">{t.hint}</div>}
          </div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-gray-100 rounded-xl p-5">
          <h3 className="font-display font-semibold mb-4">Recent inquiries</h3>
          {stats.recent_inquiries.length === 0 ? (
            <div className="text-sm text-muted-foreground">No inquiries yet.</div>
          ) : (
            <div className="space-y-2">
              {stats.recent_inquiries.map((i) => (
                <Link to="/admin/inquiries" key={i.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-md hover:bg-muted/30">
                  <div>
                    <div className="text-sm font-medium">{i.name} — {i.organization}</div>
                    <div className="text-xs text-muted-foreground">{i.items.length} products · {new Date(i.created_at).toLocaleString()}</div>
                  </div>
                  <Badge className="bg-secondary/10 text-secondary border-0">{i.status}</Badge>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <h3 className="font-display font-semibold mb-4">Inventory by category</h3>
          <div className="space-y-2">
            {stats.inventory_by_category.map((c) => (
              <div key={c.category} className="text-sm flex justify-between">
                <span>{capitalizeFirst(c.category)}</span><span className="font-semibold">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Products admin ---
const emptyProduct = {
  name: "", gemLink: "", category_slug: "enzymes", subcategory_slug: "",
  description: "", hsnCode: "", specifications: {}, packSizes: [],
  _packRows: [{ packSize: "", catalogNumber: "", inventoryQty: "", packInfo: "" }],
  images: [], documents: [], featured: false, new_arrival: false,
};

const BULK_FIELD_ALIASES = {
  name: ["name*"],
  catalogNumber: ["catalognumber*"],
  catalogNumbers: ["catalognumbers*"],
  category_slug: ["category*"],
  subcategory_slug: ["subcategory*"],
  packSizes: ["packsizes*"],
  packInfo: ["packinfo"],
  inventory_qty: ["inventoryquantity*"],
  inventory_qtys: ["inventoryquantities*"],
  gemLink: ["gemlink"],
  hsnCode: ["hsnCode"],
  specifications: ["technicalspecifications"],
  description: ["description*"],
  featured: ["featured"],
  new_arrival: ["newarrival"],
};

const getPackRows = (packSizes) => {
  if (!Array.isArray(packSizes)) {
    return [{ packSize: "", catalogNumber: "", inventoryQty: "", packInfo: "" }];
  }
  return packSizes.map((item) => ({
    packSize: item?.packSize?.toString?.() || item?.pack_size?.toString?.() || "",
    catalogNumber: item?.catalogNumber || item?.catalog_number || "",
    inventoryQty: item?.inventoryQty != null ? item.inventoryQty : item?.inventory_qty != null ? item.inventory_qty : "",
    packInfo: item?.packInfo || item?.pack_info || "",
    availability: item?.availability || "",
  }));
};

const DEFAULT_PRODUCT_IMAGE = "https://nivilabs.bionivid.com/resources/images/assest.static.default.product.png";

const normalizeHeader = (header) => header.trim().toLowerCase().replace(/[\s_]+/g, "");

const detectDelimiter = (text) => {
  const header = text.split(/\r\n|\n|\r/)[0] || "";
  const tabCount = (header.match(/\t/g) || []).length;
  const commaCount = (header.match(/,/g) || []).length;
  if (tabCount > commaCount) return "\t";
  if (commaCount > 0) return ",";
  return "\t";
};

const parseDelimited = (text, delimiter) => {
  const rows = [];
  let current = "";
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      row.push(current);
      current = "";
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') {
        i += 1;
      }
      row.push(current);
      rows.push(row);
      row = [];
      current = "";
    } else {
      current += char;
    }
  }
  if (current !== "" || row.length > 0) {
    row.push(current);
    rows.push(row);
  }
  return rows.filter((r) => r.length > 0 && !(r.length === 1 && r[0] === ""));
};

const getBulkValue = (normalizedRow, aliases) => {
  for (const alias of aliases) {
    const value = normalizedRow[normalizeHeader(alias)];
    if (value) return value;
  }
  return "";
};

const capitalizeFirst = (value) => {
  const text = String(value ?? "").trim();
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "";
};

const parseBulkSpecifications = (value) => {


  const raw = String(value ?? "").trim();

  try {
    const parsed = JSON.parse(raw);
    return {
      raw,
      specifications: parsed,
      status: "parsed",
      error: null
    };
  } catch (err) {
    console.error("JSON PARSE ERROR:", err);
    return {
      raw,
      specifications: {},
      status: "invalid",
      error: "Invalid JSON format"
    };
  }
};

const parseBulkList = (value) => String(value || "").split(",").map((item) => item.trim());

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const resolveCategorySlug = (rawValue, cats) => {
  const key = normalizeText(rawValue);
  if (!key || !Array.isArray(cats)) return "";
  const category = cats.find((c) => normalizeText(c.slug) === key || normalizeText(c.name) === key);
  return category ? category.slug : "";
};

const resolveSubcategorySlug = (rawValue, categorySlug, cats) => {
  const key = normalizeText(rawValue);
  if (!key || !Array.isArray(cats)) return "";

  if (categorySlug) {
    const category = cats.find((c) => c.slug === categorySlug);
    if (category) {
      const subcategory = (category.subcategories || []).find((s) => normalizeText(s.slug) === key || normalizeText(s.name) === key);
      if (subcategory) return subcategory.slug;
    }
  }

  const allMatches = [];
  cats.forEach((category) => {
    (category.subcategories || []).forEach((subcategory) => {
      if (normalizeText(subcategory.slug) === key || normalizeText(subcategory.name) === key) {
        allMatches.push(subcategory.slug);
      }
    });
  });

  return allMatches.length === 1 ? allMatches[0] : "";
};

const buildBulkPackSizes = (data) => {
  const packSizes = parseBulkList(data.packSizes);
  const catalogNumbers = parseBulkList(data.catalogNumbers || data.catalogNumber);
  const inventoryQtys = parseBulkList(data.inventory_qtys || data.inventory_qty);
  const packInfos = String(data.packInfo || data.packInfos || "").trim() === "" ? [] : parseBulkList(data.packInfo || data.packInfos);

  return packSizes.map((packSize, index) => ({
    packSize,
    catalogNumber: catalogNumbers.length === packSizes.length ? catalogNumbers[index] : catalogNumbers[0] || data.catalogNumber || "",
    inventoryQty: Number(inventoryQtys.length === packSizes.length ? inventoryQtys[index] : inventoryQtys[0] || data.inventory_qty || 0),
    packInfo: packInfos.length === packSizes.length && /^na$/i.test(packInfos[index]) ? "" : (packInfos[index] || ""),
  }));
};

const buildBulkRow = (rawRow, rowIndex, cats = []) => {
  const normalizedRow = {};
  Object.entries(rawRow).forEach(([key, value]) => {
    normalizedRow[normalizeHeader(key)] = String(value ?? "").trim();
  });
  const specsValue = getBulkValue(normalizedRow, BULK_FIELD_ALIASES.specifications);
  const specs = parseBulkSpecifications(specsValue);
  const rawCategory = getBulkValue(normalizedRow, BULK_FIELD_ALIASES.category_slug);
  const rawSubcategory = getBulkValue(normalizedRow, BULK_FIELD_ALIASES.subcategory_slug);
  const categorySlug = resolveCategorySlug(rawCategory, cats);
  const subcategorySlug = resolveSubcategorySlug(rawSubcategory, categorySlug, cats);

  return {
    rowIndex,
    selected: true,
    raw: rawRow,
    data: {
      name: getBulkValue(normalizedRow, BULK_FIELD_ALIASES.name),
      catalogNumbers: getBulkValue(normalizedRow, BULK_FIELD_ALIASES.catalogNumbers),
      category_raw: rawCategory,
      subcategory_raw: rawSubcategory,
      category_slug: categorySlug,
      subcategory_slug: subcategorySlug,
      packSizes: getBulkValue(normalizedRow, BULK_FIELD_ALIASES.packSizes),
      packInfo: getBulkValue(normalizedRow, BULK_FIELD_ALIASES.packInfo),
      inventory_qtys: getBulkValue(normalizedRow, BULK_FIELD_ALIASES.inventory_qtys),
      description: getBulkValue(normalizedRow, BULK_FIELD_ALIASES.description),
      hsnCode: getBulkValue(normalizedRow, BULK_FIELD_ALIASES.hsnCode),
      gemLink: getBulkValue(normalizedRow, BULK_FIELD_ALIASES.gemLink),
      specificationsRaw: specs.raw,
      specifications: specs.specifications,
      specificationsStatus: specs.status,
      specificationsError: specs.error,
    },
    status: "pending",
    message: "",
  };
};

export function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [bulkUploadRows, setBulkUploadRows] = useState([]);
  const [bulkUploadFileName, setBulkUploadFileName] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [showBulkUploadInfo, setShowBulkUploadInfo] = useState(false);
  const [showBulkUploadCategoryReference, setShowBulkUploadCategoryReference] = useState(false);
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [showBulkDeleteMode, setShowBulkDeleteMode] = useState(false);
  const [bulkDeleteRows, setBulkDeleteRows] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const bulkSelectedCount = bulkUploadRows.filter((row) => row.selected).length;
  const allBulkSelected = bulkUploadRows.length > 0 && bulkSelectedCount === bulkUploadRows.length;
  const bulkDeleteSelectedCount = bulkDeleteRows.filter((row) => row.selected).length;
  const allBulkDeleteSelected = bulkDeleteRows.length > 0 && bulkDeleteSelectedCount === bulkDeleteRows.length;
  const toggleBulkSelectAll = () => {
    const next = !allBulkSelected;
    setBulkUploadRows(bulkUploadRows.map((row) => ({ ...row, selected: next })));
  };
  const toggleBulkRowSelection = (rowIndex) => {
    setBulkUploadRows(bulkUploadRows.map((row) => row.rowIndex === rowIndex ? { ...row, selected: !row.selected } : row));
  };
  const toggleBulkDeleteSelectAll = () => {
    const next = !allBulkDeleteSelected;
    setBulkDeleteRows(bulkDeleteRows.map((row) => ({ ...row, selected: next })));
  };
  const toggleBulkDeleteRowSelection = (productId) => {
    setBulkDeleteRows(bulkDeleteRows.map((row) => row.id === productId ? { ...row, selected: !row.selected } : row));
  };

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/products", { params: { limit: 500, search: search || undefined } })
      .then((r) => setProducts(r.data.items))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { load(); api.get("/fullSlugs").then((r) => setCats(r.data)); }, [load]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const getBulkError = (data) => {
    if (!data.name) return "Name is required";
    if (!data.catalogNumber && !data.catalogNumbers) return "Catalog number is required";
    if (!data.packSizes) return "Pack sizes are required";
    const packSizesParts = parseBulkList(data.packSizes);
    if (!packSizesParts.length) {
      return "Pack sizes must be separated by commas";
    }
    if (data.hsnCode && !/^[0-9]{1,16}$/.test(data.hsnCode)) {
      return "HSN Code must be up to 16 digits";
    }

    const catalogNumbers = parseBulkList(data.catalogNumbers || data.catalogNumber);
    if (!catalogNumbers.length) return "Catalog numbers are required";
    if (!catalogNumbers.every((part) => part.trim())) {
      return "Catalog numbers must be provided and cannot be empty";
    }

    const inventoryValues = parseBulkList(data.inventory_qtys || data.inventory_qty);
    if (!inventoryValues.length) return "Inventory quantities are required";
    if (!inventoryValues.every((part) => /^[0-9]+$/.test(part))) {
      return "Inventory quantities must be numbers separated by commas";
    }

    const packInfos = String(data.packInfo || data.packInfos || "").trim() === "" ? [] : parseBulkList(data.packInfo || data.packInfos);
    if (packInfos.some((part) => part && !/^[a-zA-Z0-9 ]+$/.test(part))) {
      return "Pack info must be alphanumeric";
    }
    if (packInfos.length > 0 && packInfos.length !== packSizesParts.length) {
      return "Pack info must have the same number of comma-separated values as pack sizes. Use NA for pack sizes without pack info.";
    }

    const distinctLengths = Array.from(new Set([packSizesParts.length, catalogNumbers.length, inventoryValues.length]));
    if (distinctLengths.length > 1) {
      return "Catalog numbers, pack sizes, and inventory quantities must all have the same number of values";
    }

    if (!data.category_slug) return "Category is required";
    if (!data.subcategory_slug) return "Subcategory is required";
    if (!data.description) return "Description is required";
    return null;
  };

  const handleBulkCsvFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".tsv") && !name.endsWith(".txt")) {
      toast.error("Please upload a CSV or TSV file");
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const csvText = String(loadEvent.target?.result || "");
      const delimiter = detectDelimiter(csvText);
      const rows = delimiter === "\t" ? csvText.split(/\r\n|\n|\r/).filter(Boolean).map(line => line.split("\t"))
        : parseDelimited(csvText, delimiter);
      if (rows.length < 2) {
        toast.error("CSV needs a header row and at least one data row");
        event.target.value = "";
        return;
      }
      const headers = rows[0].map((cell) => String(cell || "").trim());
      const parsedRows = rows.slice(1).map((cells, idx) => {
        const rawRow = {};
        headers.forEach((header, index) => {
          rawRow[header] = cells[index] ?? "";
        });
        return buildBulkRow(rawRow, idx + 1, cats);
      });
      setBulkUploadRows(parsedRows);
      setBulkUploadFileName(file.name);
      setShowBulkUploadInfo(true);
      setBulkUploading(false);
      event.target.value = "";
    };
    reader.onerror = () => { toast.error("Unable to read CSV file"); event.target.value = ""; };
    reader.readAsText(file, "UTF-8");
  };

  const handleBulkSave = async () => {
    if (!bulkUploadRows.length) {
      toast.error("Upload a CSV file first");
      return;
    }
    const rowsToSave = bulkUploadRows.filter((row) => row.selected);
    if (!rowsToSave.length) {
      toast.error("Select at least one row to save");
      return;
    }
    setBulkUploading(true);
    const updatedRows = bulkUploadRows.map((row) => row.selected ? { ...row, status: "uploading", message: "Uploading..." } : row);
    setBulkUploadRows(updatedRows);
    let anySuccess = false;
    for (let index = 0; index < updatedRows.length; index += 1) {
      const row = updatedRows[index];
      if (!row.selected) continue;
      const error = getBulkError(row.data);
      if (error) {
        row.status = "failed";
        row.message = error;
        setBulkUploadRows([...updatedRows]);
        continue;
      }
      setBulkUploadRows([...updatedRows]);
      try {
        const packSizes = buildBulkPackSizes(row.data);
        const payload = {
          name: row.data.name,
          catalogNumber: row.data.catalogNumber || packSizes[0]?.catalogNumber || "",
          gemLink: row.data.gemLink,
          hsnCode: row.data.hsnCode || "",
          category_slug: row.data.category_slug,
          subcategory_slug: row.data.subcategory_slug || "",
          description: row.data.description || "",
          packSizes,
          specifications: row.data.specificationsStatus === "parsed" ? row.data.specifications : {},
          images: [DEFAULT_PRODUCT_IMAGE],
          documents: [],
          inventory_qty: packSizes.reduce((sum, pack) => sum + (Number(pack.inventoryQty) || 0), 0),
          featured: !!row.data.featured,
          new_arrival: !!row.data.new_arrival,
        };
        await api.post("/admin/products", { ...payload, slug: undefined });
        row.status = "success";
        row.message = "Uploaded";
        anySuccess = true;
      } catch (uploadError) {
        const message = uploadError?.response?.data?.detail || uploadError.message || "Upload failed";
        row.status = "failed";
        row.message = String(message);
      }
      setBulkUploadRows([...updatedRows]);
    }
    setBulkUploading(false);
    // Decide whether to close the preview: close only when all selected rows succeeded
    const selectedCount = updatedRows.filter((r) => r.selected).length;
    const successCount = updatedRows.filter((r) => r.selected && r.status === "success").length;
    if (selectedCount > 0 && successCount === selectedCount) {
      toast.success("Bulk upload complete");
      load();
      // clear and close bulk upload UI
      setBulkUploadRows([]);
      setBulkUploadFileName("");
      setShowBulkUploadInfo(false);
    } else if (anySuccess) {
      // Partial success: show message and keep preview open so admin can inspect failures
      toast.success("Bulk upload complete (some rows failed)");
      load();
    } else {
      toast.error("No rows were uploaded");
    }
  };

  const clearBulkUpload = () => {
    setBulkUploadRows([]);
    setBulkUploadFileName("");
    setBulkUploading(false);
  };

  const clearBulkDelete = () => {
    setBulkDeleteRows([]);
    setBulkDeleting(false);
  };

  const initBulkDelete = () => {
    const deleteRows = products.map((p) => ({
      id: p.id,
      name: p.name,
      category: p.category_slug,
      subcategory: p.subcategory_slug,
      selected: false,
      status: "pending",
      message: "",
    }));
    setBulkDeleteRows(deleteRows);
    setShowBulkDeleteMode(true);
  };

  const handleBulkDelete = async () => {
    if (!bulkDeleteRows.length) {
      toast.error("No products available for deletion");
      return;
    }
    const rowsToDelete = bulkDeleteRows.filter((row) => row.selected);
    if (!rowsToDelete.length) {
      toast.error("Select at least one product to delete");
      return;
    }
    if (!window.confirm(`Delete ${rowsToDelete.length} product(s)? This action cannot be undone.`)) {
      return;
    }
    setBulkDeleting(true);
    const updatedRows = bulkDeleteRows.map((row) => row.selected ? { ...row, status: "deleting", message: "Deleting..." } : row);
    setBulkDeleteRows(updatedRows);
    let anySuccess = false;
    for (let index = 0; index < updatedRows.length; index += 1) {
      const row = updatedRows[index];
      if (!row.selected) continue;
      try {
        await api.delete(`/admin/products/${row.id}`);
        row.status = "success";
        row.message = "Deleted";
        anySuccess = true;
      } catch (deleteError) {
        const message = deleteError?.response?.data?.detail || deleteError.message || "Delete failed";
        row.status = "failed";
        row.message = String(message);
      }
      setBulkDeleteRows([...updatedRows]);
    }
    setBulkDeleting(false);
    // Check if all selected rows succeeded
    const selectedCount = updatedRows.filter((r) => r.selected).length;
    const successCount = updatedRows.filter((r) => r.selected && r.status === "success").length;
    if (selectedCount > 0 && successCount === selectedCount) {
      toast.success("Bulk deletion complete");
      load();
      // close bulk delete UI
      setBulkDeleteRows([]);
      setShowBulkDeleteMode(false);
    } else if (anySuccess) {
      // Partial success
      toast.success("Bulk deletion complete (some rows failed)");
      load();
    } else {
      toast.error("No products were deleted");
    }
  };

  const save = async () => {
    if (isSaving) return;
    try {
      if (!editing?.name?.trim()) {
        toast.error("Name is required");
        return;
      }
      if (!editing?.category_slug) {
        toast.error("Category is required");
        return;
      }
      if (!editing?.description) {
        toast.error("Description is required");
        return;
      }
      if (!editing?.subcategory_slug) {
        toast.error("Subcategory is required");
        return;
      }
      if (editing?.hsnCode && !/^[0-9]{1,16}$/.test(editing.hsnCode)) {
        toast.error("HSN Code must be up to 16 digits");
        return;
      }

      const packRows = editing._packRows || [];
      const validPackRows = packRows.filter((row) => {
        const packSize = String(row.packSize || "").trim();
        const catalogNumber = String(row.catalogNumber || "").trim();
        const inventoryQty = row.inventoryQty !== "" && row.inventoryQty != null ? String(row.inventoryQty).trim() : "";
        return packSize && catalogNumber && inventoryQty !== "" && !Number.isNaN(Number(inventoryQty));
      });
      const hasPartialPackRow = packRows.some((row) => {
        const packSize = String(row.packSize || "").trim();
        const catalogNumber = String(row.catalogNumber || "").trim();
        const inventoryQty = row.inventoryQty !== "" && row.inventoryQty != null ? String(row.inventoryQty).trim() : "";
        const anyFilled = Boolean(packSize || catalogNumber || inventoryQty || row.packInfo);
        const allFilled = Boolean(packSize && catalogNumber && inventoryQty !== "" && !Number.isNaN(Number(inventoryQty)));
        return anyFilled && !allFilled;
      });
      if (hasPartialPackRow) {
        toast.error("Each pack size row must include pack size, catalog number, and inventory quantity or be left blank.");
        return;
      }
      if ((editing._packRows || []).some((row) => row.packInfo && !/^[a-zA-Z0-9 ]+$/.test(String(row.packInfo)))) {
        toast.error("Pack info must be alphanumeric");
        return;
      }
      if (!validPackRows.length) {
        toast.error("At least one pack size entry is required.");
        return;
      }

      setIsSaving(true);
      const invalidSpec = (editing._specRows || []).some((row) => {
        const key = (row.key || "").trim();
        const value = (row.value || "").trim();
        return (key && !value) || (!key && value);
      });
      if (invalidSpec) {
        toast.error("Technical specifications must have both key and value or be left blank");
        return;
      }

      const invalidResource = (editing._resourceRows || []).some((row) => {
        const name = (row.name || "").trim();
        const hasFileOrUrl = Boolean(row.file || (row.url || "").trim());
        return (name && !hasFileOrUrl) || (!name && hasFileOrUrl);
      });
      if (invalidResource) {
        toast.error("Technical resources must have both a name and a PDF file or be left blank");
        return;
      }

      let images = editing.images || [];
      let allocatedId = editing.id;
      if (editing._imageFile) {
        const fd = new FormData();
        fd.append("file", editing._imageFile);
        if (!editing._isNew && editing.id) fd.append("product_id", editing.id);
        const { data: up } = await api.post("/admin/products/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        images = [up.url];
        allocatedId = up.product_id;
      }
      if (!images.length) images = [DEFAULT_PRODUCT_IMAGE];

      // Build documents list only from the resource rows the user edited.
      // This prevents deleted/removed original documents from being re-sent.
      let documents = [];
      if (editing._resourceRows && editing._resourceRows.length) {
        for (const r of editing._resourceRows) {
          if (r.file) {
            const fdDoc = new FormData();
            fdDoc.append("file", r.file);
            if (!editing._isNew && editing.id) fdDoc.append("product_id", editing.id);
            const { data: upDoc } = await api.post("/admin/products/upload-document", fdDoc, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            documents.push({ name: r.name || r.file.name, url: upDoc.url });
          } else if (r.url) {
            documents.push({ name: r.name || "", url: r.url });
          }
        }
      }

      const specs = {};
      (editing._specRows || []).forEach((r) => {
        const k = (r.key || "").trim();
        const v = (r.value || "").trim();
        if (k) specs[k] = v;
      });

      const payload = {
        name: editing.name,
        gemLink: editing.gemLink,
        category_slug: editing.category_slug,
        subcategory_slug: editing.subcategory_slug || "",
        description: editing.description || "",
        hsnCode: editing.hsnCode?.trim() || "",
        packSizes: validPackRows.map((row) => ({
          packSize: String(row.packSize || "").trim(),
          catalogNumber: String(row.catalogNumber || "").trim(),
          inventoryQty: Number(row.inventoryQty),
          packInfo: String(row.packInfo || "").trim(),
          ...(row.availability ? { availability: row.availability } : {}),
        })),
        specifications: specs,
        images,
        documents,
        featured: !!editing.featured,
        new_arrival: !!editing.new_arrival,
      };
      if (editing._isNew) {
        await api.post("/admin/products", { ...payload, id: allocatedId, slug: undefined });
      } else {
        await api.patch(`/admin/products/${editing._origId}`, payload);
      }
      toast.success("Saved"); setOpenDialog(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setIsSaving(false);
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load();
  };
  const getCategoryLabel = (slug)=>{  
    if(cats.length !== 0){
      return cats.filter(item => item.slug === slug)[0]?.name || "";
    }
  }
  const getSubCategoryLabel = (catSlug, subCatSlug) =>{
    if(cats.length !== 0){
      let [catObj] = cats.filter(item => item.slug === catSlug) || {};
      return catObj.subcategories.filter(item => item.slug === subCatSlug)[0].name || ""
    }
  }
  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Products</h1>
          <p className="text-sm text-muted-foreground">{products.length} products</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-56" data-testid="adm-product-search" />
          <Button onClick={() => {
            setEditing({ ...emptyProduct, _isNew: true, _specRows: [{ key: "", value: "" }], _resourceRows: [{ name: "", file: null, url: "" }], _imageFile: null, _imagePreview: "" });
            setOpenDialog(true);
          }} className="bg-primary hover:bg-primary/90" data-testid="adm-add-product">
            <Plus className="w-4 h-4 mr-1" /> Add product
          </Button>
          <input
            type="file"
            accept=".csv,.tsv,text/csv,text/tab-separated-values,text/plain"
            id="bulk-upload-csv"
            className="hidden"
            onChange={handleBulkCsvFile}
            data-testid="adm-bulk-upload-input"
          />
          <button type="button" onClick={() => setShowBulkUploadInfo(true)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-background hover:bg-muted text-sm" data-testid="adm-bulk-upload-btn">
            <Upload className="w-4 h-4" /> Bulk upload
          </button>
          <button type="button" onClick={initBulkDelete} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-background hover:bg-muted text-sm" data-testid="adm-bulk-delete-btn">
            <Trash2 className="w-4 h-4" /> Bulk delete
          </button>
        </div>
      </div>

      {showBulkUploadInfo && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">Bulk upload instructions</div>
              <p className="mt-2 text-sm text-slate-700">
                Download the <span className="font-medium">template.tsv</span> file, update it with your product information, then upload the file below. The template includes two example rows that you can delete before uploading.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc list-inside">
                <li>Keep the header row intact and use the exact column names from the template.</li>
                <li>Required columns: <span className="font-medium">name*</span>,<span className="font-medium">description*</span>, <span className="font-medium">category*</span>, <span className="font-medium">subcategory*</span>, <span className="font-medium">catalognumbers*</span>, <span className="font-medium">packsizes*</span>, <span className="font-medium">inventoryquantities*</span>.</li>
                <li>For multi-pack products, supply comma-separated values for <span className="font-medium">catalognumbers*</span>, <span className="font-medium">packsizes*</span>, and <span className="font-medium">inventoryquantities*</span>.</li>
                <li>If you include <span className="font-medium">packinfo</span>, the number of values must match <span className="font-medium">packsizes</span>. Use <span className="font-medium">NA</span> for pack sizes that do not need pack info, for example: <span className="font-medium">NA,packinfotext,NA</span>.</li>
                <li>Each pack's catalog number and inventory quantity must align with the pack size position. For example, the first catalog number corresponds to the first pack size and first inventory quantity.</li>
                <li>If you upload the same product row more than once, duplicate products will be created. Do not upload duplicate rows.</li>
                <li>Rows that fail validation will remain visible in the preview so you can fix them before saving.</li>
              </ul>
              <div className="mt-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-slate-900">Category reference</div>
                  <button
                    type="button"
                    onClick={() => setShowBulkUploadCategoryReference((prev) => !prev)}
                    className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-3 text-sm text-foreground hover:bg-muted"
                  >
                    {showBulkUploadCategoryReference ? "Hide reference" : "Show reference"}
                  </button>
                </div>
                {showBulkUploadCategoryReference && (
                  <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                    <table className="min-w-full text-sm border-collapse">
                      <thead className="bg-slate-100 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 text-left border border-slate-200">Category</th>
                          <th className="px-3 py-2 text-left border border-slate-200">Category slug</th>
                          <th className="px-3 py-2 text-left border border-slate-200">Subcategory</th>
                          <th className="px-3 py-2 text-left border border-slate-200">Subcategory slug</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cats.length ? cats.flatMap((category) => {
                          const subcategories = category.subcategories || [];
                          return subcategories.length > 0 ? subcategories.map((subcategory) => (
                            <tr key={`${category.slug}-${subcategory.slug}`} className="odd:bg-slate-50">
                              <td className="px-3 py-2 border border-slate-200">{category.name}</td>
                              <td className="px-3 py-2 border border-slate-200 font-mono text-xs text-muted-foreground">{category.slug}</td>
                              <td className="px-3 py-2 border border-slate-200">{subcategory.name}</td>
                              <td className="px-3 py-2 border border-slate-200 font-mono text-xs text-muted-foreground">{subcategory.slug}</td>
                            </tr>
                          )) : [
                            <tr key={category.slug} className="odd:bg-slate-50">
                              <td className="px-3 py-2 border border-slate-200">{category.name}</td>
                              <td className="px-3 py-2 border border-slate-200 font-mono text-xs text-muted-foreground">{category.slug}</td>
                              <td className="px-3 py-2 border border-slate-200 text-muted-foreground">No subcategories</td>
                              <td className="px-3 py-2 border border-slate-200">—</td>
                            </tr>
                          ];
                        }) : (
                          <tr>
                            <td className="px-3 py-3 border border-slate-200 text-sm text-muted-foreground" colSpan={4}>
                              Loading category reference...
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <a href="/resources/files/template.tsv" download className="inline-flex h-fit items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-primary/90">
              Download template.tsv
            </a>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label htmlFor="bulk-upload-csv" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 cursor-pointer">
                Upload TSV file
              </label>
              <span className="text-sm text-slate-600">{bulkUploadFileName || "No file selected yet"}</span>
            </div>
          </div>
        </div>
      )}
      {bulkUploadRows.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">{bulkUploadFileName || "CSV preview"}</div>
              <div className="text-xs text-muted-foreground">
                {bulkUploadRows.length} rows read{bulkSelectedCount !== bulkUploadRows.length ? ` · ${bulkSelectedCount} selected` : ""}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={handleBulkSave} className="bg-primary hover:bg-primary/90" disabled={bulkUploading} data-testid="adm-bulk-save">
                {bulkUploading ? "Uploading..." : "Save"}
              </Button>
              <button type="button" onClick={clearBulkUpload} className="text-sm text-muted-foreground hover:text-foreground" data-testid="adm-bulk-clear">
                Clear
              </button>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-300 border-collapse">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left p-2 border border-gray-300">
                    <input
                      type="checkbox"
                      checked={allBulkSelected}
                      onChange={toggleBulkSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="text-left p-2 border border-gray-300">#</th>
                  <th className="text-left p-2 border border-gray-300">Name*</th>
                  <th className="text-left p-2 border border-gray-300">Description*</th>
                  <th className="text-left p-2 border border-gray-300">Category*</th>
                  <th className="text-left p-2 border border-gray-300">Subcategory*</th>
                  <th className="text-left p-2 border border-gray-300">Pack catalog nos</th>
                  <th className="text-left p-2 border border-gray-300">Pack sizes*</th>
                  <th className="text-left p-2 border border-gray-300">Pack info</th>
                  <th className="text-left p-2 border border-gray-300">Pack qtys*</th>
                  <th className="text-left p-2 border border-gray-300">HSN Code</th>
                  <th className="text-left p-2 border border-gray-300">Gem link</th>
                  <th className="text-left p-2 border border-gray-300">Specifications</th>
                  <th className="text-left p-2 border border-gray-300">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bulkUploadRows.map((row) => (
                  <tr key={row.rowIndex} className="bg-white">
                    <td className="p-2 border border-gray-300">
                      <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={() => toggleBulkRowSelection(row.rowIndex)}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="p-2 font-mono text-xs border border-gray-300">{row.rowIndex}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.name || "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.description || "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.category_slug ? getCategoryLabel(row.data.category_slug) : "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.subcategory_slug ? getSubCategoryLabel(row.data.category_slug, row.data.subcategory_slug) : "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.catalogNumbers || row.data.catalogNumber || "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.packSizes || "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.packInfo || "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.inventory_qtys || row.data.inventory_qty || "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.hsnCode || "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.data.gemLink || "Not present in this file for this row"}</td>
                    <td className="p-2 align-top max-w-[300px] border border-gray-300">
                      {row.data.specificationsStatus === "missing" ? (
                        <div className="text-xs text-muted-foreground">Not present in this file for this row</div>
                      ) : row.data.specificationsStatus === "invalid" ? (
                        <div className="text-xs text-destructive">{row.data.specificationsError}</div>
                      ) : Object.keys(row.data.specifications).length > 0 ? (
                        <div className="text-xs text-foreground/80">
                          {Object.keys(row.data.specifications).length} field{Object.keys(row.data.specifications).length === 1 ? "" : "s"} present
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">No specifications provided</div>
                      )}
                    </td>

                    <td className={`p-2 text-xs border border-gray-300 ${row.status === "failed" ? "text-destructive" : row.status === "success" ? "text-primary" : "text-muted-foreground"}`}>
                      {row.status === "pending" ? "Pending" : row.status === "uploading" ? "Uploading..." : row.status === "success" ? "Success" : "Failed"}
                      {row.message ? ` - ${row.message}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showBulkDeleteMode && bulkDeleteRows.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">Bulk delete products</div>
              <div className="text-xs text-muted-foreground">
                {bulkDeleteRows.length} product{bulkDeleteRows.length !== 1 ? "s" : ""} available{bulkDeleteSelectedCount !== bulkDeleteRows.length ? ` · ${bulkDeleteSelectedCount} selected` : ""}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={handleBulkDelete} className="bg-destructive hover:bg-destructive/90" disabled={bulkDeleting} data-testid="adm-bulk-delete-save">
                {bulkDeleting ? "Deleting..." : "Delete selected"}
              </Button>
              <button type="button" onClick={clearBulkDelete} className="text-sm text-muted-foreground hover:text-foreground" data-testid="adm-bulk-delete-clear">
                Cancel
              </button>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border border-gray-300 border-collapse">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left p-2 border border-gray-300">
                    <input
                      type="checkbox"
                      checked={allBulkDeleteSelected}
                      onChange={toggleBulkDeleteSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="text-left p-2 border border-gray-300">Product name</th>
                  <th className="text-left p-2 border border-gray-300">Category</th>
                  <th className="text-left p-2 border border-gray-300">Subcategory</th>
                  <th className="text-left p-2 border border-gray-300">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bulkDeleteRows.map((row) => (
                  <tr key={row.id} className="bg-white">
                    <td className="p-2 border border-gray-300">
                      <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={() => toggleBulkDeleteRowSelection(row.id)}
                        disabled={row.status !== "pending"}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                      />
                    </td>
                    <td className="p-2 truncate border border-gray-300">{row.name || "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.category ? capitalizeFirst(row.category) : "—"}</td>
                    <td className="p-2 truncate border border-gray-300">{row.subcategory ? capitalizeFirst(row.subcategory) : "—"}</td>
                    <td className={`p-2 text-xs border border-gray-300 ${row.status === "failed" ? "text-destructive" : row.status === "success" ? "text-primary" : "text-muted-foreground"}`}>
                      {row.status === "pending" ? "Pending" : row.status === "deleting" ? "Deleting..." : row.status === "success" ? "Deleted" : "Failed"}
                      {row.message ? ` - ${row.message}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">
            Loading product list…
          </div>
        ) : products.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">
            No products found. Add a product or try a different search.
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Product</th>
                    <th className="text-left p-3">Category</th>
                    <th className="text-left p-3">Subcategory</th>
                    <th className="text-left p-3">Created At</th>
                    <th className="text-left p-3">Pack sizes</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <React.Fragment key={p.id}>
                      <tr className="border-t border-gray-100 hover:bg-muted/20">
                        <td className="p-3">
                        <Link to={`/product/${p.id}`} className="flex items-center gap-3 text-foreground no-underline hover:text-primary transition">
                          <img src={p.images?.[0] || DEFAULT_PRODUCT_IMAGE} className="w-10 h-10 rounded object-cover" alt="" />
                          <span className="font-medium">{p.name}</span>
                        </Link>
                      </td>
                        <td className="p-3">{getCategoryLabel(p.category_slug)}</td>
                        <td className="p-3">{p.subcategory_slug ? getSubCategoryLabel(p.category_slug, p.subcategory_slug) : "—"}</td>
                        <td className="p-3 text-xs text-muted-foreground">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                        <td className="p-3">
                          <button
                            type="button"
                            onClick={() => setExpandedProductId(expandedProductId === p.id ? null : p.id)}
                            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-foreground shadow-sm hover:border-primary hover:text-primary transition"
                            data-testid={`adm-pack-toggle-${p.id}`}
                          >
                            {expandedProductId === p.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            Pack sizes
                          </button>
                        </td>
                        <td className="p-3 text-right">
                          <button onClick={(e) => { e.stopPropagation(); const rows = Object.entries(p.specifications || {}).map(([k, v]) => ({ key: k, value: String(v) })); const docRows = (p.documents || []).map((d) => (typeof d === "string" ? { name: "", url: d, file: null } : { name: d.name || "", url: d.url || "", file: null })); setEditing({ ...p, _origId: p.id, _specRows: rows.length ? rows : [{ key: "", value: "" }], _resourceRows: docRows.length ? docRows : [{ name: "", file: null, url: "" }], _packRows: getPackRows(p.packSizes), _imageFile: null, _imagePreview: p.images?.[0] || "", }); setOpenDialog(true); }} className="text-primary p-1.5" data-testid={`adm-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); del(p.id); }} className="text-destructive p-1.5" data-testid={`adm-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                      {expandedProductId === p.id && (
                        <tr className="bg-slate-50">
                          <td colSpan={6} className="p-3 border-t border-gray-100">
                            {Array.isArray(p.packSizes) && p.packSizes.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100 text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                      <th className="text-left px-3 py-2">Pack</th>
                                      <th className="text-left px-3 py-2">Catalog No</th>
                                      <th className="text-left px-3 py-2">Info</th>
                                      <th className="text-left px-3 py-2">Quantity</th>
                                      <th className="text-left px-3 py-2">Availability</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {p.packSizes.map((item, index) => (
                                      <tr key={`${p.id}-${item.packSize || index}`} className="border-t border-gray-200 bg-white">
                                        <td className="px-3 py-2 text-sm">{item.packSize || item.pack_size || "—"}</td>
                                        <td className="px-3 py-2 text-sm text-muted-foreground">{item.catalogNumber || item.catalog_number || "—"}</td>
                                        <td className="px-3 py-2 text-sm">{item.packInfo || item.pack_info || "—"}</td>
                                        <td className="px-3 py-2 text-sm">{item.inventoryQty != null ? item.inventoryQty : item.inventory_qty != null ? item.inventory_qty : "—"}</td>
                                        <td className="px-3 py-2">
                                          {item.availability ? (
                                            <Badge className={item.availability === "in_stock" ? "bg-primary/10 text-primary border-0" : "bg-destructive/10 text-destructive border-0"}>
                                              {item.availability.replace(/_/g, " ")}
                                            </Badge>
                                          ) : "—"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground">No pack size details available.</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3 p-4">
              {products.map((p) => (
                <div key={p.id} className="rounded-xl border border-gray-200 bg-slate-50 p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <img src={p.images?.[0] || DEFAULT_PRODUCT_IMAGE} className="w-14 h-14 rounded object-cover" alt="" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground truncate">{p.name}</div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span className="rounded-full border border-slate-200 bg-white px-2 py-1">{capitalizeFirst(p.category_slug)}</span>
                        <span className="rounded-full border border-slate-200 bg-white px-2 py-1">{p.subcategory_slug ? capitalizeFirst(p.subcategory_slug) : "—"}</span>
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">Created {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-3">
                    <div className="flex gap-2">
                      <button onClick={() => {
                        const rows = Object.entries(p.specifications || {}).map(([k, v]) => ({ key: k, value: String(v) }));
                        const docRows = (p.documents || []).map((d) => (typeof d === "string" ? { name: "", url: d, file: null } : { name: d.name || "", url: d.url || "", file: null }));
                        setEditing({
                          ...p,
                          _origId: p.id,
                          _specRows: rows.length ? rows : [{ key: "", value: "" }],
                          _resourceRows: docRows.length ? docRows : [{ name: "", file: null, url: "" }],
                          _packRows: getPackRows(p.packSizes),
                          _imageFile: null,
                          _imagePreview: p.images?.[0] || DEFAULT_PRODUCT_IMAGE,
                        });
                        setOpenDialog(true);
                      }} className="inline-flex h-9 items-center justify-center rounded-md bg-white px-3 text-primary shadow-sm hover:bg-slate-100" data-testid={`adm-edit-${p.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => del(p.id)} className="inline-flex h-9 items-center justify-center rounded-md bg-white px-3 text-destructive shadow-sm hover:bg-slate-100" data-testid={`adm-del-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="adm-product-dialog">
          <DialogHeader><DialogTitle>{editing?._isNew ? "Add product" : "Edit product"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Name *"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="dlg-name" /></F>
              <F label="HSN Code">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={16}
                  value={editing.hsnCode || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 16);
                    setEditing({ ...editing, hsnCode: value });
                  }}
                  data-testid="dlg-hsn-code"
                />
              </F>
              <F label="Category *">
                <Select value={editing.category_slug} onValueChange={(v) => setEditing({ ...editing, category_slug: v, subcategory_slug: "" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Subcategory *">
                <Select value={editing.subcategory_slug} onValueChange={(v) => setEditing({ ...editing, subcategory_slug: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {(cats.find((c) => c.slug === editing.category_slug)?.subcategories || []).map((s) => (
                      <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <div className="sm:col-span-1">
                <F label="GEM Link "><Input value={editing.gemLink} onChange={(e) => setEditing({ ...editing, gemLink: e.target.value })} data-testid="dlg-gem-link" /></F>
              </div>
              <div className="sm:col-span-1">
                <Label className="text-xs">Product image</Label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-md border border-gray-200 bg-muted/40 overflow-hidden flex items-center justify-center shrink-0">
                    {editing._imagePreview ? (
                      <img src={editing._imagePreview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Upload className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                      id="dlg-image-file"
                      data-testid="dlg-image-file"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 8 * 1024 * 1024) { toast.error("Image must be <= 8 MB"); return; }
                        const url = URL.createObjectURL(f);
                        setEditing({ ...editing, _imageFile: f, _imagePreview: url });
                      }}
                    />
                    <label htmlFor="dlg-image-file"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-background hover:bg-muted text-sm cursor-pointer">
                      <Upload className="w-4 h-4" /> {editing._imageFile ? "Replace image" : (editing._imagePreview ? "Change image" : "Upload image")}
                    </label>
                    {editing._imageFile && (
                      <div className="text-[11px] text-muted-foreground mt-1 truncate">{editing._imageFile.name}</div>
                    )}
                    {!editing._imageFile && (
                      <div className="text-[11px] text-muted-foreground mt-1">{editing._isNew ? "PNG / JPG / WebP, ≤ 8 MB" : "Leave as-is to keep current image"}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2">
                <F label="Description*"><Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="dlg-desc" /></F>
              </div>


              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Pack size rows</Label>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, _packRows: [...(editing._packRows || []), { packSize: "", catalogNumber: "", inventoryQty: "", packInfo: "" }] })}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    data-testid="dlg-pack-row-add"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add row
                  </button>
                </div>
                <div className="space-y-2">
                  {(editing._packRows || [{ packSize: "", catalogNumber: "", inventoryQty: "", packInfo: "" }]).map((row, idx) => (
                    <div key={idx} className="grid sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end" data-testid={`dlg-pack-row-${idx}`}>
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Pack size</Label>
                        <Input
                          type="text"
                          placeholder="e.g. 100 U"
                          value={row.packSize}
                          onChange={(e) => {
                            const next = [...(editing._packRows || [])];
                            next[idx] = { ...next[idx], packSize: e.target.value };
                            setEditing({ ...editing, _packRows: next });
                          }}
                          data-testid={`dlg-pack-size-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Catalog number</Label>
                        <Input
                          placeholder="Catalog number"
                          value={row.catalogNumber}
                          onChange={(e) => {
                            const next = [...(editing._packRows || [])];
                            next[idx] = { ...next[idx], catalogNumber: e.target.value };
                            setEditing({ ...editing, _packRows: next });
                          }}
                          data-testid={`dlg-catalog-number-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Pack info</Label>
                        <Input
                          placeholder="pack info"
                          value={row.packInfo}
                          onChange={(e) => {
                            const next = [...(editing._packRows || [])];
                            next[idx] = { ...next[idx], packInfo: e.target.value };
                            setEditing({ ...editing, _packRows: next });
                          }}
                          data-testid={`dlg-pack-info-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Inventory quantity</Label>
                        <Input
                          type="number"
                          placeholder="Qty"
                          value={row.inventoryQty}
                          onChange={(e) => {
                            const next = [...(editing._packRows || [])];
                            next[idx] = { ...next[idx], inventoryQty: e.target.value };
                            setEditing({ ...editing, _packRows: next });
                          }}
                          data-testid={`dlg-inventory-qty-${idx}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const next = (editing._packRows || []).filter((_, i) => i !== idx);
                          setEditing({ ...editing, _packRows: next.length ? next : [{ packSize: "", catalogNumber: "", inventoryQty: "", packInfo: "" }] });
                        }}
                        className="w-9 h-9 rounded-md border border-input hover:bg-muted text-muted-foreground flex items-center justify-center"
                        data-testid={`dlg-pack-row-remove-${idx}`}
                        aria-label="Remove pack row"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dynamic specifications editor */}
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Technical specifications</Label>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, _specRows: [...(editing._specRows || []), { key: "", value: "" }] })}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    data-testid="dlg-spec-add"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add row
                  </button>
                </div>
                <div className="space-y-2">
                  {(editing._specRows || []).map((row, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2" data-testid={`dlg-spec-row-${idx}`}>
                      <Input
                        placeholder="Key (e.g. Concentration)"
                        value={row.key}
                        onChange={(e) => {
                          const next = [...editing._specRows];
                          next[idx] = { ...next[idx], key: e.target.value };
                          setEditing({ ...editing, _specRows: next });
                        }}
                        data-testid={`dlg-spec-key-${idx}`}
                      />
                      <Input
                        placeholder="Value (e.g. 20,000 U/mL)"
                        value={row.value}
                        onChange={(e) => {
                          const next = [...editing._specRows];
                          next[idx] = { ...next[idx], value: e.target.value };
                          setEditing({ ...editing, _specRows: next });
                        }}
                        data-testid={`dlg-spec-value-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = editing._specRows.filter((_, i) => i !== idx);
                          setEditing({ ...editing, _specRows: next.length ? next : [{ key: "", value: "" }] });
                        }}
                        className="w-9 h-9 rounded-md border border-input hover:bg-muted text-muted-foreground flex items-center justify-center"
                        data-testid={`dlg-spec-remove-${idx}`}
                        aria-label="Remove specification"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technical Resources editor */}
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Technical Resources</Label>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, _resourceRows: [...(editing._resourceRows || []), { name: "", file: null, url: "" }] })}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    data-testid="dlg-resource-add"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {(editing._resourceRows || []).map((row, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`dlg-resource-row-${idx}`}>
                      <Input
                        placeholder="File name (e.g. Datasheet)"
                        value={row.name}
                        onChange={(e) => {
                          const next = [...editing._resourceRows];
                          next[idx] = { ...next[idx], name: e.target.value };
                          setEditing({ ...editing, _resourceRows: next });
                        }}
                        data-testid={`dlg-resource-name-${idx}`}
                      />
                      <div>
                        <input
                          type="file"
                          accept="application/pdf"
                          id={`dlg-resource-file-${idx}`}
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            if (f.size > 8 * 1024 * 1024) { toast.error("File must be <= 8 MB"); return; }
                            const next = [...editing._resourceRows];
                            next[idx] = { ...next[idx], file: f, url: URL.createObjectURL(f) };
                            setEditing({ ...editing, _resourceRows: next });
                          }}
                        />
                        <label htmlFor={`dlg-resource-file-${idx}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-background hover:bg-muted text-sm cursor-pointer">
                          <Upload className="w-4 h-4" /> {row.file ? "Replace file" : (row.url ? "Change file" : "Upload PDF")}
                        </label>
                        <div className="text-[11px] text-muted-foreground mt-1 truncate">
                          {row.file ? row.file.name : row.url ? (row.name ? `${row.name}` : row.url) : (editing._isNew ? "PDF, ≤ 8 MB" : "Optional")}
                        </div>
                        {row.url && !row.file && (
                          <div className="text-xs mt-1"><a href={resolveBackendUrl(row.url)} target="_blank" rel="noreferrer" className="text-primary underline">{row.name || 'View'}</a></div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const next = editing._resourceRows.filter((_, i) => i !== idx);
                          setEditing({ ...editing, _resourceRows: next.length ? next : [{ name: "", file: null, url: "" }] });
                        }}
                        className="w-9 h-9 rounded-md border border-input hover:bg-muted text-muted-foreground flex items-center justify-center"
                        data-testid={`dlg-resource-remove-${idx}`}
                        aria-label="Remove resource"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3 sm:col-span-2 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.new_arrival} onChange={(e) => setEditing({ ...editing, new_arrival: e.target.checked })} /> New arrival</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={save} className="bg-primary" disabled={isSaving} data-testid="dlg-save">
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => (<div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>);

// --- Inquiries admin ---
const STATUSES = ["new", "contacted", "quote_sent", "negotiating", "converted", "closed"];

export function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/admin/inquiries", { params: filter ? { status: filter } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}/status`, { status });
    toast.success("Status updated"); load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };
  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.post(`/admin/inquiries/${selected.id}/notes`, { note });
    setSelected(data); setNote(""); toast.success("Note added");
  };

  return (
    <div data-testid="admin-inquiries-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold">Inquiries</h1>
          <p className="text-sm text-muted-foreground">{items.length} inquiries</p>
        </div>
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-gray-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left p-3">Customer</th><th className="text-left p-3">Items</th><th className="text-left p-3">Status</th><th className="text-left p-3">When</th></tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} onClick={() => setSelected(i)} className={`border-t border-gray-100 cursor-pointer hover:bg-muted/30 ${selected?.id === i.id ? "bg-primary/5" : ""}`} data-testid={`adm-inq-${i.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{i.name}</div>
                    <div className="text-xs text-muted-foreground">{i.organization}</div>
                  </td>
                  <td className="p-3">{i.items.length}</td>
                  <td className="p-3"><Badge className="bg-secondary/10 text-secondary border-0">{i.status.replace("_", " ")}</Badge></td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          {selected ? (
            <div data-testid="adm-inq-detail">
              <div className="text-xs text-muted-foreground">Inquiry detail</div>
              <h3 className="font-display font-bold mt-1">{selected.name}</h3>
              <div className="text-sm text-muted-foreground">{selected.email} · {selected.phone}</div>
              <div className="text-sm">{selected.organization}</div>
              {selected.address && <div className="text-xs text-muted-foreground mt-1">{selected.address}</div>}
              <div className="mt-4">
                <Label className="text-xs">Status</Label>
                <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                  <SelectTrigger className="mt-1" data-testid="adm-inq-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="mt-4">
                <div className="text-xs font-medium mb-2">Products ({selected.items.length})</div>
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="min-w-full text-sm text-left">
                    <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Catalog number</th>
                        <th className="px-3 py-2">Pack size</th>
                        <th className="px-3 py-2">Quantity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selected.items.map((it, idx) => (
                        <tr key={idx} className="bg-white">
                          <td className="px-3 py-2 text-sm text-foreground">{it.name}</td>
                          <td className="px-3 py-2 text-sm text-muted-foreground font-mono">{it.catalogNumber || it.catalog_number || "—"}</td>
                          <td className="px-3 py-2 text-sm text-muted-foreground">{it.packSize || it.selectedPackSize || it.pack_size || "—"}</td>
                          <td className="px-3 py-2 text-sm font-semibold">{it.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {selected.notes && <div className="mt-3 text-xs"><span className="font-medium">Customer notes:</span> {selected.notes}</div>}
              <div className="mt-4 border-t pt-4">
                <Label className="text-xs">Internal notes</Label>
                <div className="space-y-1.5 mt-2 max-h-32 overflow-y-auto">
                  {(selected.admin_notes || []).map((n) => (
                    <div key={n.id} className="text-xs p-2 bg-muted/40 rounded">
                      <div className="font-semibold">{n.by} · {new Date(n.at).toLocaleString()}</div>
                      <div>{n.note}</div>
                    </div>
                  ))}
                </div>
                <Textarea rows={2} className="mt-2" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add note…" data-testid="adm-inq-note-input" />
                <Button onClick={addNote} size="sm" className="mt-2 bg-primary" data-testid="adm-inq-note-add">Add note</Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Select an inquiry to view details.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminCategories() {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  return (
    <div data-testid="admin-categories-page">
      <h1 className="font-display text-3xl font-bold mb-5">Categories</h1>
      <div className="grid md:grid-cols-2 gap-4">
        {cats.map((c) => (
          <div key={c.slug} className="bg-white border border-gray-100 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <img src={c.image_url} className="w-14 h-14 rounded-md object-cover" alt="" />
              <div>
                <div className="font-display font-bold text-lg">{c.name}</div>
                {/* <div className="text-xs text-muted-foreground font-mono">{c.slug}</div> */}
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3">{c.description}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {c.subcategories?.map((s) => <Badge key={s.slug} variant="outline" className="text-xs">{s.name}</Badge>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  return (
    <div data-testid="admin-users-page">
      <h1 className="font-display text-3xl font-bold mb-5">Users ({users.length})</h1>
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Org</th><th className="text-left p-3">Role</th><th className="text-left p-3">Joined</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-gray-100">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3 text-muted-foreground">{u.email}</td>
                <td className="p-3">{u.organization || "—"}</td>
                <td className="p-3"><Badge className={u.role === "admin" ? "bg-primary/10 text-primary border-0" : "bg-secondary/10 text-secondary border-0"}>{u.role}</Badge></td>
                <td className="p-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}